import * as classNames from "classnames";
import { ArticleCommentRenderProps } from "../articleCommentWrapper";
import { getTranslation } from "../translations";
import { AddCommentFormWrapper, AddCommentFormRenderProps } from "../addCommentFormWrapper";
import { AddCommentFormUI2026 } from "./ynetAddCommentFormUI2026";
import { separateHourFromDateAndFormatToString } from "../../../../../widgetsUtils";


interface ArticleCommenUI2026Props {
    props: ArticleCommentRenderProps
}

interface ArticleCommenUI2026State {
    lineHeightToParent: number
    allItemsOfParentCollapsed: boolean
    hasChilds: boolean
    lineToNumberInParent: number,
    isExtended: boolean,
    isOverflowing: boolean
}

export class ArticleCommentUI2026 extends React.Component<ArticleCommenUI2026Props, ArticleCommenUI2026State> {
    textRef: any;

    constructor(props: ArticleCommenUI2026Props) {
        super(props);
        this.state = {
            lineHeightToParent: 0,
            allItemsOfParentCollapsed: true,
            hasChilds: false,
            lineToNumberInParent: 0,
            isExtended: false,
            isOverflowing: false
        };
    }

    componentDidMount() {
        const { comment: { level, id }, notPopupMode } = this.props.props;

        // Only set up height calculation for level 2 comments
        if (level === 2) {
            // Delay initial calculation to ensure parent DOM is ready
            setTimeout(() => {
                this.updateLineHeight();
            }, 100);

            // Retry if parent not found initially (it might load later)
            setTimeout(() => {
                if (this.state.lineHeightToParent === 0) {
                    this.updateLineHeight();
                }
            }, 500);

            // One more retry
            setTimeout(() => {
                if (this.state.lineHeightToParent === 0) {
                    this.updateLineHeight();
                }
            }, 1000);

            // Add a listener for parent comment changes
            window.addEventListener('commentToggled', this.handleCommentToggled);
        }

        // Popup mounts while display:none (height=0). Recalculate reply-count line when it opens.
        if (!notPopupMode) {
            window.addEventListener('commentsPopupOpened', this.handlePopupOpened);
        }

        const allBrothers = notPopupMode ? document.querySelectorAll(`.SiteArticleComments >.commentsContainer [data-parent-id="${id}"]`) : document.querySelectorAll(`.ArticleCommentsPopup .commentsContainer [data-parent-id="${id}"]`);
        const brothersLengthMin = notPopupMode ? 0 : 1;
        if (allBrothers.length > brothersLengthMin) {
            this.setState({ hasChilds: true })
        }
        if (this.state.allItemsOfParentCollapsed && allBrothers.length > brothersLengthMin) {
            allBrothers.forEach(item => {
                (item as HTMLElement).style.display = "none";
            })
        }
        else {
            allBrothers.forEach(item => {
                (item as HTMLElement).style.display = "block";
            })
        }
        this.calculateLineToNumberInParent();
        this.checkOverflow();

    }

    componentDidUpdate(prevProps: ArticleCommenUI2026Props) {
        // Recalculate if the parent's expanded state might have changed
        const prevExpanded = prevProps.props.isExpanded;
        const currentExpanded = this.props.props.isExpanded;

        if (prevExpanded !== currentExpanded) {
            // Give time for DOM to update
            setTimeout(() => this.updateLineHeight(), 100);
            setTimeout(() => this.calculateLineToNumberInParent(), 100);
        }
        this.checkOverflow();
    }

    componentWillUnmount() {
        window.removeEventListener('commentToggled', this.handleCommentToggled);
        window.removeEventListener('commentsPopupOpened', this.handlePopupOpened);
    }

    private getAuthorInitials = (name?: string | null, lang?: string): string => {
        const anonymous = getTranslation(lang, "Anonymous");
        const cleanName = name && name.trim();

        const emojiRegex = /[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}]/u;
        const invalidFirstCharRegex = /^[^a-zA-Zא-תа-яА-ЯёЁ]/u;

        if (!cleanName || emojiRegex.test(cleanName[0]) || invalidFirstCharRegex.test(cleanName)) {
            return anonymous.trim().slice(0, 2).toUpperCase();
        }

        const words = cleanName.split(/\s+/);

        if (words.length === 1) {
            return cleanName === anonymous
                ? words[0].slice(0, 2).toUpperCase()
                : words[0][0].toUpperCase();
        }

        return (words[0][0] + words[1][0]).toUpperCase();
    };

    private checkAuthor = (author, lang?: string): string => {
        const anonymous = getTranslation(lang, "Anonymous");
        const cleanName = author && author.trim();
        if (!cleanName) {
            author = anonymous;
        }
        return author;
    }

    private checkOverflow = () => {
        const textRef: HTMLDivElement | null = null;
        const el = this.textRef;

        if (!el) return;

        const styles = window.getComputedStyle(el);
        const lineHeight =
            styles.lineHeight === "normal" ? parseFloat(styles.fontSize) * 1.2 : parseFloat(styles.lineHeight);

        const lines = el.scrollHeight / lineHeight;
        const isOverflowing = lines > 5;
        if (isOverflowing !== this.state.isOverflowing) {
            this.setState({ isOverflowing });
        }
    };

    private handlePopupOpened = () => {
        const { level } = this.props.props.comment;
        if (level === 2) {
            // Recalculate after popup opens with multiple delays to handle animations
            setTimeout(() => this.updateLineHeight(), 100);
            setTimeout(() => this.updateLineHeight(), 300);
        }
        // Reply-count line: remount height is 0 under display:none
        setTimeout(() => this.calculateLineToNumberInParent(), 100);
    }

    private handleCommentToggled = (event: any) => {
        const { level, talkback_parent_id, id } = this.props.props.comment;
        // If this is a level 2 comment and the toggled comment is its parent
        if (level === 2 && talkback_parent_id) {
            const parentId = talkback_parent_id.talkbackId;
            if (event.detail && event.detail.commentId === parentId) {
                // Delay to allow parent DOM to update and animations to complete
                setTimeout(() => this.updateLineHeight(), 50);
                setTimeout(() => this.updateLineHeight(), 100);
                //     setTimeout(() => this.updateLineHeight(), 200);
            }
        }
    }
    private calculateLineToNumberInParent = () => {
        const { comment: { id }, notPopupMode } = this.props.props;
        // Use data-comment-id (not #id) — same id exists in page list + popup; #id resolves to the first only
        const scope = notPopupMode
            ? document.querySelector(`.SiteArticleComments > .commentsContainer`)
            : document.querySelector(`.ArticleCommentsPopup .commentsContainer`);
        const commentEl = scope && scope.querySelector(`article[data-comment-id="${id}"]`) as HTMLElement | null;
        const itemHeight = commentEl && commentEl.offsetHeight || 0;
        if (itemHeight <= 0) {
            return;
        }
        this.setState({ lineToNumberInParent: itemHeight - 10 });
    }
    private updateLineHeight = () => {
        const { comment: { level, talkback_parent_id, id }, notPopupMode } = this.props.props;
        if (level !== 2 || !talkback_parent_id) {
            return;
        }
        const parentId = talkback_parent_id.talkbackId;

        // Get the current comment element first
        const currentCommentRef = notPopupMode ? document.querySelector(`.commentsContainer [data-comment-id="${id}"]`) : document.querySelector(`.ArticleCommentsPopup [data-comment-id="${id}"]`) as HTMLElement;
        if (!currentCommentRef) {
            return; // Can't calculate without current element
        }

        // Find parent in the same container as current element
        const container = currentCommentRef.closest('.ArticleCommentsPopup, .commentsContainer');
        let parentCommentRef: HTMLElement | null = null;

        if (container) {
            parentCommentRef = notPopupMode ? document.querySelector(`.commentsContainer [data-comment-id="${parentId}"]`) as HTMLElement : document.querySelector(`.ArticleCommentsPopup [data-comment-id="${parentId}"]`) as HTMLElement;
        }

        // Fallback to global search if not found in container
        if (!parentCommentRef) {
            parentCommentRef = notPopupMode ? document.querySelector(`.commentsContainer [data-comment-id="${parentId}"]`) as HTMLElement : document.querySelector(`.ArticleCommentsPopup [data-comment-id="${parentId}"]`) as HTMLElement;
        }


        if (parentCommentRef && currentCommentRef) {
            // Get all siblings (other level 2 comments with same parent) that appear before this one
            const allSiblings = notPopupMode
                ? Array.from(document.querySelectorAll(`.commentsContainer [data-parent-id="${parentId}"]`)) as HTMLElement[]
                : Array.from(document.querySelectorAll(`.ArticleCommentsPopup [data-parent-id="${parentId}"]`)) as HTMLElement[];

            // Find the index of current comment among visible siblings
            const visibleSiblings = allSiblings.filter(sibling => {
                const display = window.getComputedStyle(sibling).display;
                return display !== 'none';
            });

            const currentIndex = visibleSiblings.findIndex(sibling =>
                sibling.getAttribute('data-comment-id') === id.toString()
            );

            // Calculate the distance from parent to current comment
            const parentRect = parentCommentRef.getBoundingClientRect();
            const currentRect = currentCommentRef.getBoundingClientRect();

            let newHeight;

            // Always calculate from parent's top to current comment's top
            // This gives consistent behavior for single or multiple children
            newHeight = currentRect.top - parentRect.top;

            // Add safety checks
            if (newHeight < 0) {
                newHeight = 0;
            } else if (newHeight > 2000) {
                newHeight = parentRect.height;
            }

            if (newHeight === 0 && parentRect.height > 0) {
                newHeight = parentRect.height;
            }

            if (this.state.lineHeightToParent !== newHeight || this.state.lineHeightToParent === 0) {
                this.setState({ lineHeightToParent: newHeight });
            }
        }
    }
    private handleToggleOpenedState = () => {
        const { toggleOpenedState, comment, handleCommentsPopupVisibility, notPopupMode } = this.props.props;
        if (notPopupMode) {
            handleCommentsPopupVisibility()
        }
        else {
            // Call the toggle first
            toggleOpenedState();
            // Dispatch event so child comments know to recalculate AFTER toggle
            setTimeout(() => {
                if (typeof window !== 'undefined') {
                    const event = new CustomEvent('commentToggled', {
                        detail: { commentId: comment.id }
                    });
                    window.dispatchEvent(event);
                }
            }, 0);
        }
    }

    private handleFormVisibilityWrapper = (formType: any) => {
        const { handleFormVisibility, comment, notPopupMode, handleCommentsPopupVisibility } = this.props.props;
        if (notPopupMode) {
            handleCommentsPopupVisibility()
        }
        else {
            // Call the original function
            handleFormVisibility(formType);
            // Dispatch event so child comments know to recalculate after form visibility changes
            setTimeout(() => {
                if (typeof window !== 'undefined') {
                    const event = new CustomEvent('commentToggled', {
                        detail: { commentId: comment.id }
                    });
                    window.dispatchEvent(event);
                }
            }, 0);
        }
        // Recalculate line height after form opens with delays
        setTimeout(() => this.calculateLineToNumberInParent(), 100);
        //  setTimeout(() => this.calculateLineToNumberInParent(), 300);
    }

    private handleCloseCommentFormWrapper = () => {
        const { handleCloseCommentForm, comment } = this.props.props;
        // Call the original function
        handleCloseCommentForm();
        // Dispatch event so child comments know to recalculate after form closes
        setTimeout(() => {
            if (typeof window !== 'undefined') {
                const event = new CustomEvent('commentToggled', {
                    detail: { commentId: comment.id }
                });
                window.dispatchEvent(event);
            }
        }, 0);
        // Recalculate line height after form closes with delays
        setTimeout(() => this.calculateLineToNumberInParent(), 100);
        //   setTimeout(() => this.calculateLineToNumberInParent(), 300);
    }

    private handleClickReport = () => {
        const { comment, articleId, domain } = this.props.props;
        const reportUrl = `${domain ? domain : "https://www.ynet.co.il"}/redmail?mailType=commentReport&articleId=${articleId}&commentText=${comment.text}&mailTitle=דיווח על תגובה`;
        window.open(reportUrl, '_blank');

    }

    private openLevel2InPopupByCommentId = (commentId: number, attempt: number = 0) => {
        const btnInParentInpopup = document.querySelector(
            `.ArticleCommentsPopup .commentsContainer .level2CommentsCountWrapper[data-comment-id="${commentId}"] .commentCount`
        ) as HTMLElement;

        if (btnInParentInpopup) {
            btnInParentInpopup.click();
            return;
        }

        if (attempt < 10) {
            setTimeout(() => this.openLevel2InPopupByCommentId(commentId, attempt + 1), 100);
        }
    }

    private handleOpenaLevel2Comments = () => {
        const { comment: { id }, notPopupMode } = this.props.props;
        if (notPopupMode) {
            setTimeout(() => this.openLevel2InPopupByCommentId(id), 0);

        }
        else {
            this.setState({ allItemsOfParentCollapsed: false })
            const allBrothers = document.querySelectorAll(`.ArticleCommentsPopup .commentsContainer [data-parent-id="${id}"]`);
            allBrothers.forEach(item => {
                (item as HTMLElement).style.display = "block";
            });

            setTimeout(() => {
                if (typeof window !== 'undefined') {
                    const event = new CustomEvent('commentToggled', {
                        detail: { commentId: id }
                    });
                    window.dispatchEvent(event);
                }
            }, 0);
        }

    }
    private handleCloseLevel2Comments = () => {
        const { comment: { id }, notPopupMode } = this.props.props;
        const allBrothers = notPopupMode ? document.querySelectorAll(`.SiteArticleComments >.commentsContainer [data-parent-id="${id}"]`) : document.querySelectorAll(`.ArticleCommentsPopup .commentsContainer [data-parent-id="${id}"]`);
        const brothersLengthMin = notPopupMode ? 0 : 1;
        this.setState({ allItemsOfParentCollapsed: allBrothers.length > brothersLengthMin });
        allBrothers.length > brothersLengthMin && allBrothers.forEach(item => {
            (item as HTMLElement).style.display = "none";
        });

        setTimeout(() => {
            if (typeof window !== 'undefined') {
                const event = new CustomEvent('commentToggled', {
                    detail: { commentId: id }
                });
                window.dispatchEvent(event);
            }
        }, 0);

    }
    private toggleReplyFormVisibility = () => {
        if (this.props.props.isAddCommentFormVisible) {
            this.handleCloseCommentFormWrapper();
        }
        else {
            this.handleFormVisibilityWrapper("REPLY")
        }
    }

    render() {
        const { lang, isExpanded, vote, likes, unlikes, commentFormType, articleId, postId, notPopupMode, isAddCommentFormVisible, handleVoteButtonsClick,
            comment: { id, number, title, text, author, pubDate, level, recommended, talkback_parent_id } } = this.props.props
        const { lineHeightToParent, allItemsOfParentCollapsed, hasChilds, lineToNumberInParent } = this.state;

        const expandedCommentClasses = classNames({
            "ArticleComment2026": true,
            "expanded": isExpanded,
            "level1": level === 1,
            "level2": level === 2,
        })

        const likeClasses = classNames({
            "likeSvg": true,
            "likePressed": vote === 1,
        })
        const likesCounterClasses = classNames({
            "likesCounter": true,
            "likePressed": vote === 1,
            "redCounter": likes > unlikes ? recommended : false
        })

        const unlikesCounterClasses = classNames({
            "likesCounter unlikesCounter": true,
            "dislikePressed": vote === -1,
            "redCounter": unlikes > likes ? recommended : false
        })

        const dislikeClasses = classNames({
            "dislikeSvg": true,
            "dislikePressed": vote === -1,
        })

        const commentTitleClasses = classNames({
            "commentTitle": true,
            "redTitleText": recommended,
            "expanded": isExpanded
        })

        const commentTextStyles = classNames({
            "commentText": true,
            "level1": level === 1,
            "level2": level === 2,
            "expanded": isExpanded
        })

        const extendedBottomWrapperStyles = classNames({
            "bottomButtonsWrapper": true,
            "level1": level === 1,
            "level2": level === 2,
        })

        const commentNumClasses = classNames({
            "commentNum": true,
            //"redTitleText": recommended,
        })

        const positiveLikes = likes < 0 ? 0 : likes
        const positiveUnlikes = unlikes < 0 ? 0 : unlikes;
        const allBrothers = notPopupMode ? document.querySelectorAll(`.SiteArticleComments >.commentsContainer [data-parent-id="${id}"]`) : document.querySelectorAll(`.ArticleCommentsPopup .commentsContainer [data-parent-id="${id}"]`);
        const totalCommentsCount = allBrothers ? allBrothers.length : 0;
        const articlePublishDate = separateHourFromDateAndFormatToString(pubDate, { showHour: true, dateTimeSeperator: " | " })

        return (
            <>
                <article id={`ArticleComment2026_${id}`} className={expandedCommentClasses} data-comment-id={id} data-parent-id={level === 2 && talkback_parent_id ? talkback_parent_id.talkbackId : undefined}>
                    <div className="topView">
                        {level === 1 && <div className={commentNumClasses} title={`${number}`} onClick={() => this.handleToggleOpenedState()}>{this.getAuthorInitials(author ? author : getTranslation(lang, "Anonymous"), lang)}</div>}

                        {level === 2 && <div className="level2ArrowContainer" onClick={(e) => this.handleToggleOpenedState()}> <span className="lineToParent" style={{ height: lineHeightToParent + "px" } as React.CSSProperties} /><span className="level2Arrow" /></div>}

                        <div className="topLeftView">
                            <div className="commentDetailsWrapper" >
                                <span className="CommentDetails"><span className="author">{this.checkAuthor(author, lang)}</span><time className="DateDisplay">{articlePublishDate}</time></span>

                                <div>
                                    <div ref={(el) => (this.textRef = el)} className={`${commentTitleClasses} ${this.state.isExtended ? "expandedText" : "clampedText"}`}>
                                        {text}
                                    </div>

                                    {this.props.props.isAllCommentsPopupVisable && !this.state.isExtended && this.state.isOverflowing && (
                                        <div className="readMore" onClick={() => this.setState({ isExtended: true })}>
                                            עוד...
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="details">
                                <button className="commentToComent" title={getTranslation(lang, "Comment To Coment")} onClick={this.toggleReplyFormVisibility}>
                                    <span> {getTranslation(lang, "Join to Discussion")}</span>
                                </button>

                                <div className="voteButtonsWrapper">
                                    <button role="button" className="voteButton" onClick={(e) => handleVoteButtonsClick(true, "3state")} aria-label="Like">
                                        <span className={likesCounterClasses}>{positiveLikes}</span>

                                        {vote === 1 ?
                                            <svg className={likeClasses} width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path fillRule="evenodd" clipRule="evenodd" d="M6.68731 2.24194C6.75368 2.09482 6.90176 2 7.06516 2C7.58606 2 8.08562 2.20388 8.45395 2.5668C8.82228 2.92971 9.0292 3.42193 9.0292 3.93517V5.56479H11.539C11.7479 5.56283 11.9548 5.60545 12.1454 5.68974C12.3368 5.77438 12.5072 5.89901 12.6449 6.05499C12.7825 6.21097 12.8841 6.39458 12.9426 6.59309C13.0011 6.79159 13.0151 7.00026 12.9836 7.20463L12.2704 11.7879C12.218 12.1278 12.0427 12.4379 11.7768 12.6605C11.5115 12.8827 11.1738 13.0032 10.8256 12.9999H3.44719C3.06337 12.9999 2.69527 12.8497 2.42387 12.5823C2.15247 12.3149 2 11.9522 2 11.574V8.00922C2 7.63105 2.15247 7.26836 2.42387 7.00095C2.69527 6.73354 3.06337 6.58331 3.44719 6.58331H4.72904L6.68731 2.24194ZM4.58426 7.39812H3.44719C3.28269 7.39812 3.12494 7.4625 3.00862 7.57711C2.89231 7.69171 2.82696 7.84715 2.82696 8.00922V11.574C2.82696 11.7361 2.89231 11.8915 3.00862 12.0061C3.12494 12.1207 3.28269 12.1851 3.44719 12.1851H4.58426V7.39812ZM5.41123 12.1851H10.8325C10.9821 12.1868 11.1272 12.1352 11.2412 12.0397C11.3552 11.9443 11.4303 11.8114 11.4527 11.6657L12.166 7.08235C12.1795 6.99482 12.1735 6.90528 12.1484 6.82026C12.1234 6.73519 12.0798 6.6565 12.0208 6.58965C11.9618 6.5228 11.8888 6.46939 11.8068 6.43312C11.7247 6.39684 11.6357 6.37857 11.5458 6.37958L11.5411 6.37963L8.61572 6.3796C8.38736 6.3796 8.20224 6.1972 8.20224 5.9722V3.93517C8.20224 3.63803 8.08244 3.35307 7.86919 3.14296C7.71732 2.99331 7.5269 2.89037 7.32092 2.84352L5.41123 7.07717V12.1851Z" fill="#78787B" />
                                                <path d="M7.25 2.75L5.25 7V12.5L9.75 12.75L11.5 12.5L12.5 7.75V6.5L11.75 6H8.5V4L8.25 3L7.25 2.75Z" fill="#78787B" />
                                            </svg>
                                            :
                                            <svg className={likeClasses} xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none">
                                                <path fillRule="evenodd" clipRule="evenodd" d="M6.68731 2.24194C6.75368 2.09482 6.90176 2 7.06516 2C7.58606 2 8.08562 2.20388 8.45395 2.5668C8.82228 2.92971 9.0292 3.42193 9.0292 3.93517V5.56479H11.539C11.7479 5.56283 11.9548 5.60545 12.1454 5.68974C12.3368 5.77438 12.5072 5.89901 12.6449 6.05499C12.7825 6.21097 12.8841 6.39458 12.9426 6.59309C13.0011 6.79159 13.0151 7.00026 12.9836 7.20463L12.2704 11.7879C12.218 12.1278 12.0427 12.4379 11.7768 12.6605C11.5115 12.8827 11.1738 13.0032 10.8256 12.9999H3.44719C3.06337 12.9999 2.69527 12.8497 2.42387 12.5823C2.15247 12.3149 2 11.9522 2 11.574V8.00922C2 7.63105 2.15247 7.26836 2.42387 7.00095C2.69527 6.73354 3.06337 6.58331 3.44719 6.58331H4.72904L6.68731 2.24194ZM4.58426 7.39812H3.44719C3.28269 7.39812 3.12494 7.4625 3.00862 7.57711C2.89231 7.69171 2.82696 7.84715 2.82696 8.00922V11.574C2.82696 11.7361 2.89231 11.8915 3.00862 12.0061C3.12494 12.1207 3.28269 12.1851 3.44719 12.1851H4.58426V7.39812ZM5.41123 12.1851H10.8325C10.9821 12.1868 11.1272 12.1352 11.2412 12.0397C11.3552 11.9443 11.4303 11.8114 11.4527 11.6657L12.166 7.08235C12.1795 6.99482 12.1735 6.90528 12.1484 6.82026C12.1234 6.73519 12.0798 6.6565 12.0208 6.58965C11.9618 6.5228 11.8888 6.46939 11.8068 6.43312C11.7247 6.39684 11.6357 6.37857 11.5458 6.37958L11.5411 6.37963L8.61572 6.3796C8.38736 6.3796 8.20224 6.1972 8.20224 5.9722V3.93517C8.20224 3.63803 8.08244 3.35307 7.86919 3.14296C7.71732 2.99331 7.5269 2.89037 7.32092 2.84352L5.41123 7.07717V12.1851Z" fill="#78787B" />
                                            </svg>
                                        }
                                    </button>

                                    <button role="button" className="voteButton" onClick={(e) => handleVoteButtonsClick(false, "3state")} aria-label="Dislike">
                                        <span className={unlikesCounterClasses}>{positiveUnlikes}</span>

                                        {vote === -1 ?
                                            <svg className={dislikeClasses} width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path fillRule="evenodd" clipRule="evenodd" d="M8.31269 12.7581C8.24632 12.9052 8.09824 13 7.93484 13C7.41394 13 6.91438 12.7961 6.54605 12.4332C6.17772 12.0703 5.9708 11.5781 5.9708 11.0648L5.9708 9.43521L3.46103 9.43521C3.25212 9.43718 3.04524 9.39455 2.85462 9.31026C2.66322 9.22562 2.49279 9.10099 2.35513 8.94501C2.21747 8.78903 2.11588 8.60542 2.05739 8.40691C1.9989 8.20841 1.98491 7.99974 2.01639 7.79538L2.72964 3.21212C2.78204 2.87225 2.95735 2.5621 3.2232 2.33946C3.48852 2.11725 3.82623 1.99677 4.17438 2.00007L11.5528 2.00007C11.9366 2.00007 12.3047 2.1503 12.5761 2.41771C12.8475 2.68512 13 3.04781 13 3.42598L13 6.99078C13 7.36895 12.8475 7.73164 12.5761 7.99905C12.3047 8.26646 11.9366 8.41669 11.5528 8.41669L10.271 8.41669L8.31269 12.7581ZM10.4157 7.60188L11.5528 7.60188C11.7173 7.60188 11.8751 7.5375 11.9914 7.4229C12.1077 7.30829 12.173 7.15285 12.173 6.99078L12.173 3.42598C12.173 3.26391 12.1077 3.10847 11.9914 2.99387C11.8751 2.87926 11.7173 2.81488 11.5528 2.81488L10.4157 2.81488L10.4157 7.60188ZM9.58877 2.81488L4.16748 2.81488C4.0179 2.81321 3.87275 2.86484 3.75878 2.96029C3.6448 3.05575 3.56968 3.18857 3.54725 3.33429L2.83399 7.91765C2.82053 8.00518 2.82651 8.09472 2.85156 8.17974C2.87663 8.26481 2.92017 8.3435 2.97916 8.41035C3.03816 8.4772 3.1112 8.53061 3.19323 8.56688C3.27526 8.60316 3.36431 8.62143 3.45421 8.62042L3.4589 8.62037L6.38428 8.6204C6.61264 8.6204 6.79776 8.8028 6.79776 9.0278L6.79776 11.0648C6.79776 11.362 6.91756 11.6469 7.13081 11.857C7.28268 12.0067 7.47311 12.1096 7.67908 12.1565L9.58877 7.92283L9.58877 2.81488Z" fill="#78787B" />
                                                <path d="M7.75 12.25L9.75 8L9.75 2.5L5.25 2.25L3.5 2.5L2.5 7.25L2.5 8.5L3.25 9L6.5 9L6.5 11L6.75 12L7.75 12.25Z" fill="#78787B" />
                                            </svg>
                                            :
                                            <svg className={dislikeClasses} xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none">
                                                <path fillRule="evenodd" clipRule="evenodd" d="M8.31269 12.7581C8.24632 12.9052 8.09824 13 7.93484 13C7.41394 13 6.91438 12.7961 6.54605 12.4332C6.17772 12.0703 5.9708 11.5781 5.9708 11.0648V9.43521H3.46103C3.25212 9.43717 3.04524 9.39455 2.85462 9.31026C2.66322 9.22562 2.49279 9.10099 2.35513 8.94501C2.21747 8.78903 2.11588 8.60542 2.05739 8.40691C1.9989 8.20841 1.98491 7.99974 2.01639 7.79537L2.72964 3.21212C2.78204 2.87225 2.95735 2.5621 3.2232 2.33946C3.48851 2.11725 3.82623 1.99677 4.17437 2.00007H11.5528C11.9366 2.00007 12.3047 2.1503 12.5761 2.41771C12.8475 2.68512 13 3.04781 13 3.42598V6.99078C13 7.36895 12.8475 7.73164 12.5761 7.99905C12.3047 8.26646 11.9366 8.41669 11.5528 8.41669H10.271L8.31269 12.7581ZM10.4157 7.60188H11.5528C11.7173 7.60188 11.8751 7.5375 11.9914 7.42289C12.1077 7.30829 12.173 7.15285 12.173 6.99078V3.42598C12.173 3.26391 12.1077 3.10847 11.9914 2.99387C11.8751 2.87926 11.7173 2.81487 11.5528 2.81487H10.4157V7.60188ZM9.58877 2.81487H4.16748C4.0179 2.81321 3.87275 2.86484 3.75878 2.96029C3.6448 3.05575 3.56968 3.18857 3.54725 3.33429L2.83399 7.91765C2.82053 8.00518 2.82651 8.09472 2.85156 8.17974C2.87663 8.26481 2.92017 8.3435 2.97916 8.41035C3.03816 8.4772 3.1112 8.53061 3.19323 8.56688C3.27526 8.60316 3.36431 8.62143 3.45421 8.62042L3.45889 8.62037L6.38428 8.6204C6.61264 8.6204 6.79776 8.8028 6.79776 9.0278V11.0648C6.79776 11.362 6.91756 11.6469 7.13081 11.857C7.28268 12.0067 7.4731 12.1096 7.67908 12.1565L9.58877 7.92283V2.81487Z" fill="#78787B" />
                                            </svg>
                                        }
                                    </button>

                                    <button role="button" className="reportButton" onClick={this.handleClickReport} aria-label={getTranslation(lang, "Report")}>
                                        <svg xmlns="http://www.w3.org/2000/svg" className={"reportButton"} width="15" height="15" viewBox="0 0 15 15" fill="none">
                                            <path d="M3.125 13.75V8.75M3.125 8.75V2.5M3.125 8.75L4.66875 8.44125C5.70052 8.23566 6.76977 8.33379 7.74688 8.72375C8.8054 9.14689 9.97032 9.22584 11.0763 8.94938L11.21 8.91625C11.3999 8.86885 11.5685 8.75932 11.689 8.60508C11.8095 8.45083 11.875 8.26073 11.875 8.065V3.46063C11.875 3.34673 11.849 3.23434 11.799 3.13198C11.7491 3.02962 11.6765 2.93998 11.5867 2.86986C11.497 2.79975 11.3924 2.75099 11.281 2.7273C11.1696 2.70361 11.0543 2.7056 10.9438 2.73313C9.92411 2.98785 8.85013 2.91481 7.87437 2.52438L7.74688 2.47375C6.76996 2.08391 5.70094 1.98578 4.66938 2.19125L3.125 2.5M3.125 2.5V1.25" stroke="#78787B" strokeWidth="0.9375" strokeLinecap="round" />
                                        </svg>

                                        <span className={"reportText"} >{getTranslation(lang, "Report")}</span>
                                    </button>
                                </div>
                            </div>

                            {level === 1 && <AddCommentFormWrapper
                                lang={lang}
                                postId={postId}
                                articleId={articleId}
                                parentTalkbackId={id}
                                formType={commentFormType}
                                shouldTitleBeHidden={true}
                                isAddCommentFormVisible={isAddCommentFormVisible}
                                handleCloseCommentForm={this.handleCloseCommentFormWrapper}
                                render={(props: AddCommentFormRenderProps) => <AddCommentFormUI2026 props={{ ...props, shouldTitleBeHidden: true }} />}
                            />}

                        </div>
                    </div>

                    {level === 2 &&
                        <AddCommentFormWrapper
                            lang={lang}
                            articleId={articleId}
                            parentTalkbackId={id}
                            formType={commentFormType}
                            shouldTitleBeHidden={true}
                            isAddCommentFormVisible={isAddCommentFormVisible}
                            handleCloseCommentForm={this.handleCloseCommentFormWrapper}
                            render={(props: AddCommentFormRenderProps) => <AddCommentFormUI2026 props={{ ...props, shouldTitleBeHidden: true }} />}
                        />
                    }
                </article>

                {hasChilds && (allItemsOfParentCollapsed ?
                    <div className="level2CommentsCountWrapper" data-comment-id={id}>
                        <span className="commentToComent" />
                        <button className="commentCount" onClick={this.handleOpenaLevel2Comments}>{totalCommentsCount >= 2 ? totalCommentsCount : ""} {totalCommentsCount >= 2 ? getTranslation(lang, "Comments") : getTranslation(lang, "one comment")}</button>
                        <span className="lineToParent" style={{ height: lineToNumberInParent + "px" } as React.CSSProperties} />
                    </div>
                    :
                    <div className="level2HidecounterWrapper" data-comment-id={id}>
                        <span className="commentToComent" />
                        <button className="commentCount" onClick={this.handleCloseLevel2Comments}>{totalCommentsCount >= 2 ? getTranslation(lang, "Hide Comments") : getTranslation(lang, "Hide Comment")}</button>
                    </div>
                )}
            </>
        )
    }
}
